import { hexagramBits } from '../../iching/core/iching-core';
import { activationFromLongitude, lineStartLongitude } from './mandala-layout';
import { MANDALA_DEGREES_PER_LINE, MANDALA_GATE_SEQUENCE_CCW, MANDALA_QUARTERS, MANDALA_ZODIAC_SIGNS } from './mandala.constants';

/**
 * Seizoenen en kwartaalstarts op het Rave-wiel (lecture "Passenger, Vehicle
 * and Driver", 2026-10-21; analyse 2026-10-22 §1.10 en 4C).
 *
 * - De zonnewendes en equinoxen zijn de vier cardinale lengtes 0°, 90°, 180°
 *   en 270° (de cusps van Ram, Kreeft, Weegschaal en Steenbok). Ze vallen in
 *   de vier poorten van het Cross of the Vessel of Love: 25, 15, 46 en 10,
 *   telkens 1,75° na de poortgrens, dus in lijn 2. De aarde staat dan in de
 *   tegenoverliggende Vessel-poort. Zo markeren de Vessel-poorten het midden
 *   van elk kwartaal van het wiel.
 * - De kwartalen openen met de vier poorten van het Cross of the Sphinx:
 *   13 (Initiatie), 2 (Beschaving), 7 (Dualiteit) en 1 (Mutatie).
 *
 * Alles hier wordt afgeleid uit de wielgeometrie (`mandala-layout`), zodat de
 * poort- en lijnnummers nooit los van de constants kunnen gaan lopen.
 */

export type HdSeasonKey = 'springEquinox' | 'summerSolstice' | 'autumnEquinox' | 'winterSolstice';

/**
 * Seizoens-badge op het mandala-wiel (viewBox-eenheden): straal van het ronde
 * badge-je, afstand van zijn middelpunt tot de zichtbare wielrand, en de
 * ruimte die de chartpagina daarvoor buiten de wielrand vrijhoudt
 * (`maxOuterRadius` van de layout), zodat het badge-je nooit door de viewBox
 * geknipt wordt: rand + offset + straal + 2 marge ≤ ruimte.
 */
export const HD_SEASON_MARKER_RADIUS = 8;
export const HD_SEASON_MARKER_EDGE_OFFSET = 12;
export const HD_SEASON_MARKER_ROOM = HD_SEASON_MARKER_EDGE_OFFSET + HD_SEASON_MARKER_RADIUS + 2;

export interface HdSeasonMarker {
  readonly key: HdSeasonKey;
  /** Cardinale zodiac-lengte: 0, 90, 180 of 270. */
  readonly longitude: number;
  /** Dierenriemteken dat op deze lengte begint (`MANDALA_ZODIAC_SIGNS`-key). */
  readonly zodiacKey: string;
  /** Poort waarin de zon op dat moment staat (Vessel of Love). */
  readonly sunGate: number;
  /** Lijn van die poort waarin de cardinale lengte valt (lijn 2). */
  readonly sunLine: number;
  /** Poort van de aarde op dat moment (de tegenoverliggende Vessel-poort). */
  readonly earthGate: number;
  /** Font Awesome-icoon (solid) voor de transitweergaven. */
  readonly icon: string;
}

const SEASON_DEFINITIONS: readonly { key: HdSeasonKey; longitude: number; icon: string }[] = [
  { key: 'springEquinox', longitude: 0, icon: 'seedling' },
  { key: 'summerSolstice', longitude: 90, icon: 'sun-bright' },
  { key: 'autumnEquinox', longitude: 180, icon: 'leaf' },
  { key: 'winterSolstice', longitude: 270, icon: 'snowflake' },
];

export const HD_SEASON_MARKERS: readonly HdSeasonMarker[] = SEASON_DEFINITIONS.map(definition => {
  const sun = activationFromLongitude(definition.longitude);
  const earth = activationFromLongitude(definition.longitude + 180);
  const zodiac = MANDALA_ZODIAC_SIGNS.find(sign => sign.startLongitude === definition.longitude);
  return {
    key: definition.key,
    longitude: definition.longitude,
    zodiacKey: zodiac?.key ?? '',
    sunGate: sun.gate,
    sunLine: sun.line,
    earthGate: earth.gate,
    icon: definition.icon,
  };
});

/** Seizoensmoment waarvan de zon in deze poort valt, of null (enkel 25, 15, 46 en 10). */
export function hdSeasonBySunGate(gate: number): HdSeasonMarker | null {
  return HD_SEASON_MARKERS.find(marker => marker.sunGate === gate) ?? null;
}

/**
 * Fractie (0..1) binnen de zonlijn waarop de cardinale lengte valt. Het exacte
 * tijdstip volgt door lineair te interpoleren tussen de echte lijngrenzen van
 * de zon: binnen één lijn (±1 dag) is de zonsnelheid zo goed als constant, de
 * fout blijft onder een minuut.
 */
export function hdSeasonFractionWithinLine(marker: HdSeasonMarker): number {
  const lineStart = lineStartLongitude(marker.sunGate, marker.sunLine);
  const offset = (((marker.longitude - lineStart) % 360) + 360) % 360;
  return offset / MANDALA_DEGREES_PER_LINE;
}

/** Tijdstip (epoch-ms) van het seizoensmoment binnen het lijnsegment `[fromMs, toMs]` van de zon. */
export function hdSeasonMomentMs(marker: HdSeasonMarker, fromMs: number, toMs: number): number {
  return fromMs + hdSeasonFractionWithinLine(marker) * (toMs - fromMs);
}

export interface HdQuarterStart {
  /** Kwartaal-key uit `MANDALA_QUARTERS` (initiation, civilization, duality, mutation). */
  readonly key: string;
  /** Sphinx-poort waarmee het kwartaal opent. */
  readonly gate: number;
  /** Laatste poort van het kwartaal. */
  readonly endGate: number;
  /** Vessel-poort in het midden van het kwartaal (het seizoensmoment). */
  readonly midGate: number;
  /**
   * Onderste twee hexagramlijnen (lijn 1/lijn 2) die alle zestien poorten van het
   * kwartaal delen: Ra deelt het wiel zo in vier (Channels by Type 4, "Third Line
   * Day"). Mutatie is het enige yang/yang-kwartaal; de L&K-sectie over de driekwart
   * van het wiel (`intro.threeQuarters`) steunt daarop.
   */
  readonly bottomLines: HdQuarterBottomLines;
}

/** Lijn 1/lijn 2 van een hexagram, onderste lijn eerst. */
export type HdQuarterBottomLines = 'yang/yang' | 'yang/yin' | 'yin/yin' | 'yin/yang';

/** Onderste twee lijnen van het hexagram van een poort (King Wen-figuur, onder→boven). */
export function hdGateBottomLines(gate: number): HdQuarterBottomLines {
  const [line1, line2] = hexagramBits(gate);
  return `${line1 === '1' ? 'yang' : 'yin'}/${line2 === '1' ? 'yang' : 'yin'}` as HdQuarterBottomLines;
}

export const HD_QUARTER_STARTS: readonly HdQuarterStart[] = MANDALA_QUARTERS.map(quarter => ({
  key: quarter.key,
  gate: MANDALA_GATE_SEQUENCE_CCW[quarter.sequenceStartIndex],
  endGate: MANDALA_GATE_SEQUENCE_CCW[quarter.sequenceStartIndex + 15],
  midGate: MANDALA_GATE_SEQUENCE_CCW[quarter.sequenceStartIndex + 8],
  bottomLines: hdGateBottomLines(MANDALA_GATE_SEQUENCE_CCW[quarter.sequenceStartIndex]),
}));

/** Kwartaal dat opent wanneer de zon deze poort ingaat, of null (enkel 13, 2, 7 en 1). */
export function hdQuarterStartBySunGate(gate: number): HdQuarterStart | null {
  return HD_QUARTER_STARTS.find(quarter => quarter.gate === gate) ?? null;
}

/** Seizoensmoment in het midden van dit kwartaal. */
export function hdSeasonOfQuarter(quarter: HdQuarterStart): HdSeasonMarker | null {
  return hdSeasonBySunGate(quarter.midGate);
}
